import { Core, WebViewerInstance } from '@pdftron/webviewer';
import type { VirtualElement } from '@popperjs/core';
import { useCallback, useEffect, useState } from 'react';

import type { SuggestionAnnotationClassType } from '../annotationCommands/SuggestionAnnotation';
import type { ISuggestionAnnotation } from '../annotationCommands/SuggestionAnnotation';
import { getPDFTronLeftPanelWidth } from '../annotationCommands/suggestionUtils';

type UseSuggestionAnnotationFlyoutProps = {
  instance: WebViewerInstance | null;
  SuggestionAnnotationClass: SuggestionAnnotationClassType | null;
  isFlyoutEnabled: boolean;
};

const createVirtualReference = (
  annotation: ISuggestionAnnotation,
  webViewerInstance: WebViewerInstance,
): VirtualElement | null => {
  const rect = annotation.getRect();
  const pageNumber = annotation.getPageNumber();
  const documentViewer = webViewerInstance.Core.documentViewer;
  const displayMode = documentViewer.getDisplayModeManager().getDisplayMode();

  // Get annotation bounds in viewport coordinates
  const scrollViewElement = documentViewer.getScrollViewElement();
  const scrollViewRect = scrollViewElement.getBoundingClientRect();
  const scrollLeft = scrollViewElement.scrollLeft;
  const scrollTop = scrollViewElement.scrollTop;

  // Measure toolbar heights
  // The webviewer is a custom element (apryse-webviewer) with shadow DOM
  const iframeDoc = scrollViewElement.ownerDocument;
  let totalHeaderHeight = 0;

  // Try to find the apryse-webviewer custom element
  const webviewerElement = document.querySelector('apryse-webviewer');

  if (webviewerElement) {
    const webviewerRect = webviewerElement.getBoundingClientRect();
    // The difference between webviewer top and scrollView top is the toolbar height
    totalHeaderHeight = scrollViewRect.top - webviewerRect.top;
  } else {
    // Fallback: try to find headers in the document
    const selectors = [
      '.ModularHeader',
      '[data-element="header"]',
      '.Header',
      'header',
    ];

    for (const selector of selectors) {
      const headers = iframeDoc?.querySelectorAll(selector);
      if (headers && headers.length > 0) {
        headers.forEach((header) => {
          const headerElement = header as HTMLElement;
          if (headerElement.offsetHeight > 0) {
            totalHeaderHeight += headerElement.offsetHeight;
          }
        });
        break;
      }
    }
  }

  // pageToWindow returns coordinates from the top of the iframe window (which includes toolbars)
  // scrollViewRect.top gives us where the scrollView (PDF canvas) starts in the main viewport
  // So we need to: pageToWindow + scrollViewRect.top - toolbarHeight - scroll
  let topLeft: { x: number; y: number };
  let topRight: { x: number; y: number };
  let bottomLeft: { x: number; y: number };
  // pageToWindow throws when document is not loaded (getPageWidth before documentLoaded)
  try {
    topLeft = displayMode.pageToWindow({ x: rect.x1, y: rect.y1 }, pageNumber);
    topRight = displayMode.pageToWindow({ x: rect.x2, y: rect.y1 }, pageNumber);
    bottomLeft = displayMode.pageToWindow(
      { x: rect.x1, y: rect.y2 },
      pageNumber,
    );
  } catch {
    return null;
  }

  // PDFTron left panel throws off viewport calculations, so we need to account for it
  const leftPanelWidth = getPDFTronLeftPanelWidth();

  const toViewport = (coord: { x: number; y: number }) => ({
    x: coord.x + scrollViewRect.left - scrollLeft,
    y: coord.y + scrollViewRect.top - totalHeaderHeight - scrollTop,
  });

  const viewportTopLeft = toViewport(topLeft);
  const viewportTopRight = toViewport(topRight);
  const viewportBottomLeft = toViewport(bottomLeft);

  const width = viewportTopRight.x - viewportTopLeft.x;
  const height = viewportBottomLeft.y - viewportTopLeft.y;

  // Create a virtual reference element for Popper
  const virtualReference: VirtualElement = {
    getBoundingClientRect: () => ({
      width,
      height,
      top: viewportTopLeft.y,
      left: viewportTopLeft.x - leftPanelWidth,
      right: viewportTopLeft.x + width - leftPanelWidth,
      bottom: viewportTopLeft.y + height,
      x: viewportTopLeft.x - leftPanelWidth,
      y: viewportTopLeft.y,
      toJSON: () => ({}),
    }),
  };

  return virtualReference;
};

type FlyoutState = {
  isOpen: boolean;
  virtualReference: VirtualElement | null;
  annotation: ISuggestionAnnotation | null;
};

/**
 * This hook is responsible for managing the flyout state for suggestion annotations.
 * It creates a virtual reference for the flyout and updates it as the user pans around the
 * document.
 *
 * @param instance - The WebViewer instance
 * @param SuggestionAnnotationClass - The class for the suggestion annotations
 * @returns The flyout state and a function to close the flyout
 */
export const useSuggestionAnnotationFlyout = ({
  instance,
  SuggestionAnnotationClass,
  isFlyoutEnabled,
}: UseSuggestionAnnotationFlyoutProps) => {
  const [flyoutState, setFlyoutState] = useState<FlyoutState>({
    isOpen: false,
    virtualReference: null,
    annotation: null,
  });

  // Update flyout position when panning, scrolling, or zooming
  useEffect(() => {
    if (
      !instance ||
      !flyoutState.isOpen ||
      !flyoutState.annotation ||
      !isFlyoutEnabled
    )
      return;

    const annotation = flyoutState.annotation;

    const updateVirtualReference = () => {
      const newVirtualReference = createVirtualReference(annotation, instance);
      setFlyoutState((prev) => ({
        ...prev,
        virtualReference: newVirtualReference,
      }));
    };

    const documentViewer = instance.Core.documentViewer;
    const scrollViewElement = documentViewer.getScrollViewElement();

    // Update on scroll/pan
    scrollViewElement.addEventListener('scroll', updateVirtualReference);

    // Update on zoom
    documentViewer.addEventListener('zoomUpdated', updateVirtualReference);

    // Update on display mode change (rotation, layout change, etc)
    documentViewer.addEventListener(
      'displayModeUpdated',
      updateVirtualReference,
    );

    return () => {
      scrollViewElement.removeEventListener('scroll', updateVirtualReference);
      documentViewer.removeEventListener('zoomUpdated', updateVirtualReference);
      documentViewer.removeEventListener(
        'displayModeUpdated',
        updateVirtualReference,
      );
    };
  }, [instance, flyoutState.isOpen, flyoutState.annotation, isFlyoutEnabled]);

  /**
   * Add on select callback to show it on click
   */
  useEffect(() => {
    if (!instance || !SuggestionAnnotationClass || !isFlyoutEnabled) return;

    const handleAnnotationSelected = (
      annotations: Core.Annotations.Annotation[],
      action: string,
    ) => {
      // NB: This type coercion is ugly, but okay because the suggestion annotation class is an implementation of the ISuggestionAnnotation interface.
      const suggestionAnnotations = annotations.filter(
        (annot) => annot instanceof SuggestionAnnotationClass,
      ) as ISuggestionAnnotation[];

      if (suggestionAnnotations.length > 0 && action === 'selected') {
        const annotation = suggestionAnnotations[0];
        const virtualReference = createVirtualReference(annotation, instance);

        setFlyoutState({
          isOpen: true,
          virtualReference,
          annotation,
        });
      }
    };

    instance.Core.annotationManager.addEventListener(
      'annotationSelected.suggestionFlyout',
      handleAnnotationSelected,
    );

    return () => {
      instance.Core.annotationManager.removeEventListener(
        'annotationSelected.suggestionFlyout',
      );
    };
  }, [instance, SuggestionAnnotationClass, isFlyoutEnabled]);

  const closeFlyout = useCallback(() => {
    setFlyoutState({
      isOpen: false,
      virtualReference: null,
      annotation: null,
    });
  }, []);

  return {
    flyoutState,
    closeFlyout,
  };
};
